import { useMemo } from 'react';
import { View } from 'foundations-components/transitional/components/View';
import { LanguageIcon } from 'foundations-assets';
import { Link } from 'foundations-components/transitional/components/Link';
import { TruncateString } from 'foundations-components/transitional/components/TruncateString';
import ContentHoverActions from 'content-management-actions-lib/components/ContentHoverActions';
import { CONTENT_PLATFORM_PROPERTY_NAME, contentHasTranslations, getContentPropertyValue } from 'content-management-data-lib';
import ListingLibLabelCell from 'framework-listing-lib/table/ListingLibLabelCell';
import useTrackerProps from '../../hooks/useTrackerProps';
import { isTemporarySlug, stripProtocol } from '../../utils/urls';
import HighlightText from '../HighlightText';
import LanguageName from '../LanguageName';
import NoPreviewUrl from '../NoPreviewUrl';
import CellWithHelpText from './CellWithHelpText';
import CellWithMultiLanguageGroupControls from './CellWithMultiLanguageGroupControls';
import { useContentActions } from 'content-management-actions-lib/providers/ContentActionsProvider';
import { LISTING_LIB_INTERACTION } from '../../constants/trackerEvents';
import useCrmObjectTypeListingFilters from 'framework-listing-lib/hooks/useCrmObjectTypeListingFilters';
import { useFilterQuery } from 'framework-listing-lib/hooks/useListingLibFilters';
import useLibTracker from '../../hooks/useLibTracker';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const ContentUrl = ({
  crmObject
}) => {
  const url = getContentPropertyValue(crmObject, CONTENT_PLATFORM_PROPERTY_NAME.ABSOLUTE_URL, '');
  if (isTemporarySlug(url)) {
    return /*#__PURE__*/_jsx(NoPreviewUrl, {
      crmObject: crmObject
    });
  }
  const urlWithoutProtocol = stripProtocol(url);
  return /*#__PURE__*/_jsx(TruncateString, {
    tooltip: urlWithoutProtocol,
    children: /*#__PURE__*/_jsx(HighlightText, {
      value: urlWithoutProtocol
    })
  });
};
const NameAndLanguage = ({
  crmObject,
  shouldHighlightSearchText = true,
  shouldShowLanguageColor
}) => {
  const name = getContentPropertyValue(crmObject, CONTENT_PLATFORM_PROPERTY_NAME.NAME);
  const language = getContentPropertyValue(crmObject, CONTENT_PLATFORM_PROPERTY_NAME.LANGUAGE);
  return /*#__PURE__*/_jsxs("span", {
    "aria-hidden": true,
    children: [shouldHighlightSearchText ? /*#__PURE__*/_jsx(HighlightText, {
      value: name
    }) : name, /*#__PURE__*/_jsx(LanguageName, {
      languageKey: language,
      shouldShowLanguageColor: shouldShowLanguageColor
    })]
  });
};
const ContentLink = ({
  crmObject
}) => {
  const {
    allActions
  } = useContentActions();
  const libTracker = useLibTracker();
  const translatedFromId = getContentPropertyValue(crmObject, CONTENT_PLATFORM_PROPERTY_NAME.TRANSLATED_FROM_ID);
  const filterQuery = useFilterQuery();
  const {
    hasAppliedFilters
  } = useCrmObjectTypeListingFilters();
  const isFilteringOrSearching = useMemo(() => Boolean(filterQuery || hasAppliedFilters), [filterQuery, hasAppliedFilters]);
  const hasTranslations = contentHasTranslations(crmObject);
  const handleLinkClick = e => {
    e.stopPropagation();
    libTracker === null || libTracker === void 0 || libTracker.track(LISTING_LIB_INTERACTION, {
      action: 'clicked-content-link-edit'
    });
    if (allActions.edit && allActions.edit.onActionButtonClick) {
      const extraProps = e.metaKey ? {
        shouldOpenInNewTab: true
      } : undefined;
      allActions.edit.onActionButtonClick(crmObject, extraProps);
    }
  };
  return /*#__PURE__*/_jsxs(Link, {
    onClick: handleLinkClick,
    "data-test-id": `content-name${translatedFromId ? `-language-variation-${translatedFromId}` : ''}`,
    truncate: true,
    truncateStringProps: {
      tooltip: /*#__PURE__*/_jsx(NameAndLanguage, {
        crmObject: crmObject,
        shouldHighlightSearchText: false
      })
    },
    children: [hasTranslations && !isFilteringOrSearching && /*#__PURE__*/_jsx(View, {
      as: "span",
      marginInlineEnd: "100",
      children: /*#__PURE__*/_jsx(LanguageIcon, {
        size: "200",
        alignWithText: true,
        color: "icon-core-subtle"
      })
    }), /*#__PURE__*/_jsx(NameAndLanguage, {
      crmObject: crmObject,
      shouldShowLanguageColor: true
    })]
  });
};
const NameCell = ({
  crmObject,
  tdProps,
  helpText
}) => {
  const {
    actionsTrackerProps
  } = useTrackerProps();
  return /*#__PURE__*/_jsx(ListingLibLabelCell, Object.assign({
    "data-onboarding": "content-name-cell",
    hoverContent: /*#__PURE__*/_jsx(ContentHoverActions, {
      crmObject: crmObject,
      trackerProps: actionsTrackerProps
    }),
    tooltip: false
  }, tdProps, {
    children: /*#__PURE__*/_jsx(CellWithMultiLanguageGroupControls, {
      crmObject: crmObject,
      children: /*#__PURE__*/_jsx(CellWithHelpText, {
        helpText: helpText !== null && helpText !== void 0 ? helpText : /*#__PURE__*/_jsx(ContentUrl, {
          crmObject: crmObject
        }),
        isCell: false,
        text: /*#__PURE__*/_jsx(ContentLink, {
          crmObject: crmObject
        })
      })
    })
  }));
};
export default NameCell;